import React, { FC, memo, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { pickKeys } from '@sportworld/js-sdk/src/utils/utils'

import { addFavoritePage, removeFavoritePage } from '@/actions'
import { IconButton } from '@/components/IconButton'
import { usePagePromotionTokens } from '@/components/PageHeader/hooks/usePagePromotionTokens'
import {
  analyticsFavoriteTypes,
  eventObject,
  eventAction,
  eventCategory,
} from '@/constants/analytics'
import { focusIds } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { LoginSessionType } from '@/constants/login'
import { reducedPageKeys } from '@/constants/page'
import { paths } from '@/constants/paths'
import { usePageRefresher } from '@/hooks/usePageRefresher'
import { getCurrentLanguage } from '@/i18n'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { PageType } from '@/types/servicesTypes/pageTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'
import { sendEventToGTAG } from '@/utils/utils'

import {
  FavWrapper,
  HeaderContainer,
  HeaderElem,
  HeaderLabels,
  HeaderTextWrapper,
  LogoBox,
  PageLogo,
  PageName,
  PromotionLabel,
  PromotionTokens,
  SmallHeaderText,
} from './styles'

export type Props = {
  isPopup?: boolean
  name?: string
  logo?: string
  secondLogo?: string
  page?: PageType
}

const PageHeaderRaw: FC<Props> = ({
  name,
  logo,
  secondLogo,
  page,
  isPopup,
}) => {
  const focusIdPrefix = isPopup ? focusIds.PAGE_POPUP : ''

  const { pathname } = useLocation()
  const { t } = useCommonTranslation()
  const { isRegisteredUser } = useAuth()

  const { favoritePages } = useStore((state) => state.favoritePage)
  const setAuthPopup = useStore((state) => state.common.setAuthPopup)

  const pageTabIndex = useStore((state) => state.page.page?.tabIndex)
  const pagePopupTabIndex = useStore((state) => state.popup.pagePopup?.tabIndex)
  const tabIndex = isPopup ? pagePopupTabIndex : pageTabIndex

  const isSelectedPage = pathname.includes(paths.SELECTED_PAGE) && !isPopup
  const isSpecialPage = pathname.includes(paths.SPECIAL_HOME) && !isPopup
  const isSportsPage = pathname.includes(paths.SPORTS) && !isPopup
  const isFocusPage = pathname.includes(paths.FOCUS_PAGE) && !isPopup
  const [isFavoritesLoading, setIsFavoritesLoading] = useState(false)
  const promotionTokens = usePagePromotionTokens(page)
  const { pageRefresh } = usePageRefresher()

  const isFavoritePage = isSelectedPage
    ? false
    : Boolean(favoritePages && page?.id && favoritePages[page.id])

  const favoriteIcon =
    isRegisteredUser && isFavoritePage && !isFavoritesLoading
      ? icons.favorites.heart
      : icons.favorites.heartOutline

  const isInitFailed = isRegisteredUser && !favoritePages
  const focusId = `${focusIdPrefix}${focusIds.ICON_BUTTON}${favoriteIcon}${page?.id}`

  const hideFavoriteIcon =
    isInitFailed ||
    !page ||
    isSpecialPage ||
    isSelectedPage ||
    isSportsPage ||
    isFocusPage ||
    page.withoutFavoriteIcon

  const smallHeaderText = pathname.includes(paths.FAVORITES) ? 'FAVORITES' : ''

  const showSmallHeaderText = isFavoritePage && smallHeaderText

  const openAuthPopup = () => {
    if (!page) {
      return
    }
    setAuthPopup({
      sessionType: LoginSessionType.LOGIN,
      prevFocusId: focusId,
      redirectPath: isPopup ? `${paths.PAGE}/${page.id}` : undefined,
      title: t('addToFavorites'),
      description: t('favoritesUnavailable'),
      onSuccessHandler: () => {
        toggleFavorite()
        !isPopup && pageRefresh(`?tabIndex=${tabIndex}`)
      },
    })
  }

  const language = getCurrentLanguage()

  const toggleFavorite = async (isFavoritePage?: boolean) => {
    if (!page) {
      return
    }

    if (!isRegisteredUser) {
      openAuthPopup()
      return
    }

    setIsFavoritesLoading(true)
    const isSportType = page.categoryId !== null

    const favorite = {
      type: isSportType
        ? analyticsFavoriteTypes.SPORT
        : analyticsFavoriteTypes.CONTENT_HUB,
      details: isSportType ? page.categoryId : page.title,
      page_id: page.id,
      page_name: page.title,
    }

    try {
      isFavoritePage
        ? await removeFavoritePage(page.id)
        : await addFavoritePage(pickKeys(page, reducedPageKeys))

      sendAnalyticsEvent({
        object: eventObject.FAVORITE,
        action: isFavoritePage ? eventAction.REMOVE : eventAction.ADD,
        favorite,
      })

      sendEventToGTAG(eventCategory.FAVORITE, {
        type: 'page',
        action: isFavoritePage ? 'remove' : 'add',
        page_name: page.name,
        page_id: page.id,
      })
    } catch {
      console.error(`${isFavoritePage ? 'remove' : 'add'} FavoritePage error`)
    } finally {
      setIsFavoritesLoading(false)
    }
  }

  return (
    <HeaderContainer>
      <HeaderElem>
        {logo && (
          <LogoBox $hasSecondLogo={!!secondLogo}>
            <PageLogo src={logo} />
          </LogoBox>
        )}
        {secondLogo && (
          <LogoBox>
            <PageLogo src={secondLogo} />
          </LogoBox>
        )}
        <HeaderTextWrapper>
          {showSmallHeaderText && (
            <SmallHeaderText>{smallHeaderText}</SmallHeaderText>
          )}
          <PageName>{name}</PageName>
        </HeaderTextWrapper>
      </HeaderElem>
      <HeaderLabels>
        {!!promotionTokens && (
          <PromotionTokens>
            <PromotionLabel>
              {t('pagePromotionTokens')}:{' '}
              {promotionTokens?.toLocaleString(language)}
            </PromotionLabel>
            <img src={icons.register.swPromotionCredits} alt="" />
          </PromotionTokens>
        )}

        <FavWrapper
          $hidden={hideFavoriteIcon}
          $isFavoritePage={isFavoritePage}
          $isLoading={isFavoritesLoading}
        >
          <IconButton
            disabled={hideFavoriteIcon}
            iconSrc={favoriteIcon}
            onClick={() => toggleFavorite(isFavoritePage)}
            focusId={focusId}
          />
        </FavWrapper>
      </HeaderLabels>
    </HeaderContainer>
  )
}

export const PageHeader = memo(PageHeaderRaw)
